import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Box, IconButton } from '@mui/material';
import { SecondaryGrid } from '../../../../components/common/grid/SecondaryGrid';
import { TableHeader } from '../../../../components/common/header/tableHeader/TableHeader';
import { Eye, DocumentText1, Sms, Trash } from 'iconsax-react';
import { DeleteConfirmationDialog } from '../dialog/DeleteConfirmationDialog';
import { BrokerDialog } from '../../clients/add-new-client/dialog/BrokerDialog';
import { getBrokersAsync, deleteBrokerAsync } from '@application/features/brokers/brokerThunk';
export { useBrokerCount } from './useBrokerCount';
export const BrokerModule = () => {
    const dispatch = useAppDispatch();
    const brokers = useAppSelector((state) => state.broker.brokers) ?? [];
    const [openAddBrokerDialog, setOpenAddBrokerDialog] = useState(false);
    const [brokerDialogType, setBrokerDialogType] = useState('add');
    const [selectedBrokerId, setSelectedBrokerId] = useState('');
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [deleteItemType, setDeleteItemType] = useState(undefined);
    const [openBrokerDeleteDialog, setOpenBrokerDeleteDialog] = useState(false);
    const [deleteBrokerId, setDeleteBrokerId] = useState(null);
    const [freelancerTasks, setFreelancerTasks] = useState([
        { id: '1', name: 'Development', type: 'freelancer' },
        { id: '2', name: 'Design', type: 'freelancer' }
    ]);
    const [secondmentTasks, setSecondmentTasks] = useState([
        { id: '3', name: 'Consulting', type: 'secondment' },
        { id: '4', name: 'Management', type: 'secondment' }
    ]);
    const brokerColumns = [
        {
            field: 'companyName',
            headerName: 'Company Name',
            flex: 1,
            minWidth: 200,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Company Name' })
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            minWidth: 200,
            renderHeader: () => _jsx(TableHeader, { icon: Sms, text: 'Email' })
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            align: 'center',
            flex: 0.8,
            minWidth: 150,
            renderHeader: () => _jsx(TableHeader, { icon: DocumentText1, text: 'Actions' }),
            renderCell: (params) => (_jsxs(Box, { sx: { display: 'flex', gap: 1, justifyContent: 'center' }, children: [_jsx(IconButton, { size: 'small', "aria-label": `View broker ${params.row.companyName}`, onClick: () => {
                            setSelectedBrokerId(params.row.id);
                            setBrokerDialogType('edit');
                            setOpenAddBrokerDialog(true);
                        }, children: _jsx(Eye, {}) }), _jsx(IconButton, { size: 'small', "aria-label": `Delete broker ${params.row.companyName}`, sx: { color: '#FF0000' }, onClick: () => {
                            setDeleteBrokerId(params.row.id);
                            setOpenBrokerDeleteDialog(true);
                        }, children: _jsx(Trash, {}) })] }))
        }
    ];
    useEffect(() => {
        dispatch(getBrokersAsync());
    }, [dispatch]);
    const brokerRows = useMemo(() => brokers.map((b) => ({
        id: b.id,
        companyName: b.companyName,
        email: b.email ?? ''
    })), [brokers]);
    const confirmDelete = () => {
        if (!deleteItemId)
            return;
        if (deleteItemType === 'freelancer') {
            setFreelancerTasks(freelancerTasks.filter((item) => item.id !== deleteItemId));
        }
        else if (deleteItemType === 'secondment') {
            setSecondmentTasks(secondmentTasks.filter((item) => item.id !== deleteItemId));
        }
        setOpenDeleteDialog(false);
        setDeleteItemId(null);
        setDeleteItemType(undefined);
    };
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setDeleteItemId(null);
        setDeleteItemType(undefined);
    };
    const handleBrokerSaved = () => {
        dispatch(getBrokersAsync());
        setOpenAddBrokerDialog(false);
        setSelectedBrokerId('');
    };
    const handleConfirmBrokerDelete = async () => {
        if (!deleteBrokerId)
            return;
        try {
            await dispatch(deleteBrokerAsync(deleteBrokerId)).unwrap();
            setOpenBrokerDeleteDialog(false);
            setDeleteBrokerId(null);
        }
        catch {
            setOpenBrokerDeleteDialog(false);
            setDeleteBrokerId(null);
        }
    };
    // Display only: name the broker in the delete confirmation.
    const brokerToDeleteName = brokers.find((broker) => broker.id === deleteBrokerId)?.companyName;
    const handleCloseBrokerDeleteDialog = () => {
        setOpenBrokerDeleteDialog(false);
        setDeleteBrokerId(null);
    };
    return (_jsxs("div", { children: [_jsx(SecondaryGrid, { columns: brokerColumns, rows: brokerRows }), _jsx(BrokerDialog, { open: openAddBrokerDialog, handleClose: () => {
                    setOpenAddBrokerDialog(false);
                    setSelectedBrokerId('');
                }, type: brokerDialogType, brokerId: selectedBrokerId || undefined, onBrokerSaved: handleBrokerSaved }), _jsx(DeleteConfirmationDialog, { open: openDeleteDialog, handleClose: handleCloseDeleteDialog, onConfirm: confirmDelete, title: 'Delete Item', subTitle: 'Are you sure you want to delete this item?' }), _jsx(DeleteConfirmationDialog, { open: openBrokerDeleteDialog, handleClose: handleCloseBrokerDeleteDialog, onConfirm: handleConfirmBrokerDelete, title: 'Delete broker', subTitle: brokerToDeleteName
                    ? `"${brokerToDeleteName}" will be permanently deleted. This cannot be undone.`
                    : 'This broker will be permanently deleted. This cannot be undone.' })] }));
};
